import React, { useEffect, useMemo, useState } from 'react';
import { respondToAppointment } from '../../services/calendarService';

const RSVP_LABEL = {
  PENDING: 'Pending',
  ACCEPTED: 'Accepted',
  TENTATIVE: 'Tentative',
  REJECTED: 'Rejected',
};

const RSVP_BADGE = {
  PENDING: 'bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-200',
  ACCEPTED: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-200',
  TENTATIVE: 'bg-sky-100 text-sky-800 dark:bg-sky-900/50 dark:text-sky-200',
  REJECTED: 'bg-rose-100 text-rose-800 dark:bg-rose-900/50 dark:text-rose-200',
};

const formatWhen = (info) => {
  if (!info?.date) return '';
  try {
    const [y, m, d] = info.date.split('-').map(Number);
    const date = new Date(y, m - 1, d);
    const day = date.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
    return `${day} · ${info.startTime || ''} – ${info.endTime || ''}`;
  } catch {
    return `${info.date} · ${info.startTime || ''} – ${info.endTime || ''}`;
  }
};

const formatDayLabel = (dateStr) => {
  if (!dateStr) return '';
  try {
    const [y, m, d] = String(dateStr).slice(0, 10).split('-').map(Number);
    const date = new Date(y, m - 1, d);
    return date.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return String(dateStr).slice(0, 10);
  }
};

const addOneMonth = (dateStr) => {
  if (!dateStr) return '';
  try {
    const [y, m, d] = String(dateStr).slice(0, 10).split('-').map(Number);
    const date = new Date(y, m - 1, d);
    date.setMonth(date.getMonth() + 1);
    const yy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    return `${yy}-${mm}-${dd}`;
  } catch {
    return '';
  }
};

const ordinalDay = (day) => {
  const n = Number(day);
  if (!Number.isFinite(n)) return String(day);
  const rem100 = n % 100;
  if (rem100 >= 11 && rem100 <= 13) return `${n}th`;
  switch (n % 10) {
    case 1: return `${n}st`;
    case 2: return `${n}nd`;
    case 3: return `${n}rd`;
    default: return `${n}th`;
  }
};

const frequencyLabel = (info) => {
  const normalized = String(info?.recurrenceType || '').toLowerCase();
  if (normalized === 'daily') return 'Daily';
  if (normalized === 'weekly') {
    const days = (info.recurrenceDaysOfWeek || []).filter(Boolean);
    return days.length ? `Weekly · ${days.join(', ')}` : 'Weekly';
  }
  if (normalized === 'monthly') {
    const days = (info.recurrenceDaysOfMonth || []).map(ordinalDay).filter(Boolean);
    return days.length ? `Monthly · ${days.join(', ')}` : 'Monthly';
  }
  return '';
};

const DetailRow = ({ label, value }) => {
  if (!value) return null;
  return (
    <div className="flex gap-3 py-2 border-b border-indigo-100/80 dark:border-indigo-900/40 last:border-b-0">
      <span className="w-24 flex-shrink-0 text-[11px] font-semibold uppercase tracking-wide text-indigo-400 dark:text-indigo-400/80 pt-0.5">
        {label}
      </span>
      <span className="min-w-0 text-sm text-gray-800 dark:text-gray-100 leading-snug">
        {value}
      </span>
    </div>
  );
};

const AppointmentTimelineCard = ({ message, currentUserId, onUpdated }) => {
  const appointmentInfo = message?.appointmentInfo;
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState(false);

  const rsvps = appointmentInfo?.rsvps || [];

  const sameId = (a, b) => a != null && b != null && String(a) === String(b);

  const myRsvp = useMemo(
    () => rsvps.find((r) => sameId(r.userId, currentUserId)),
    [rsvps, currentUserId],
  );

  const counts = useMemo(() => {
    const next = { ACCEPTED: 0, REJECTED: 0, TENTATIVE: 0, PENDING: 0 };
    rsvps.forEach((r) => {
      const status = (r?.status || 'PENDING').toUpperCase();
      const key = next[status] !== undefined ? status : 'PENDING';
      next[key] += 1;
    });
    return next;
  }, [rsvps]);

  const withNames = useMemo(() => {
    const names = rsvps
      .filter((r) => r?.userName && !sameId(r.userId, appointmentInfo?.organizerId))
      .map((r) => r.userName);
    if (names.length) return names.join(', ');
    return rsvps.map((r) => r.userName).filter(Boolean).join(', ');
  }, [rsvps, appointmentInfo?.organizerId]);

  const isOrganizer = sameId(appointmentInfo?.organizerId, currentUserId);
  const isCancelled = appointmentInfo?.status === 'CANCELLED';
  const canRespond = !isCancelled && Boolean(appointmentInfo?.appointmentId)
    && (isOrganizer || Boolean(myRsvp) || rsvps.some((r) => sameId(r.userId, currentUserId)));

  const rsvpFingerprint = rsvps.map((r) => `${r.userId}:${r.status}`).join('|');
  useEffect(() => {
    if (!appointmentInfo?.appointmentId) return;
    const decided = rsvps.some((r) => r.status && r.status !== 'PENDING');
    if (decided) setExpanded(true);
  }, [appointmentInfo?.appointmentId, rsvpFingerprint]);

  if (!appointmentInfo?.appointmentId) return null;

  const handleRsvp = async (status) => {
    if (!appointmentInfo.appointmentId || saving) return;
    setSaving(true);
    setError('');
    setExpanded(true);
    try {
      const updated = await respondToAppointment(appointmentInfo.appointmentId, status);
      const nextInfo = {
        ...appointmentInfo,
        status: updated.status,
        rsvps: updated.rsvps || appointmentInfo.rsvps,
        meetingLink: updated.meetingLink || appointmentInfo.meetingLink,
      };
      if (typeof onUpdated === 'function') {
        onUpdated({
          ...message,
          appointmentInfo: nextInfo,
        });
      }
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to update RSVP');
    } finally {
      setSaving(false);
    }
  };

  const summaryParts = [];
  if (counts.ACCEPTED) summaryParts.push(`${counts.ACCEPTED} accepted`);
  if (counts.REJECTED) summaryParts.push(`${counts.REJECTED} rejected`);
  if (counts.TENTATIVE) summaryParts.push(`${counts.TENTATIVE} tentative`);
  if (counts.PENDING) summaryParts.push(`${counts.PENDING} pending`);

  const frequency = frequencyLabel(appointmentInfo);
  const untilDate = frequency
    ? formatDayLabel(appointmentInfo.recurrenceUntilDate || addOneMonth(appointmentInfo.date))
    : '';
  const myStatus = (myRsvp?.status || 'PENDING').toUpperCase();
  const hasDecided = myStatus !== 'PENDING';

  return (
    <div className="relative max-w-md w-full overflow-hidden rounded-2xl border border-indigo-200/80 dark:border-indigo-700/40 bg-gradient-to-br from-indigo-50 via-sky-50 to-white dark:from-indigo-950/70 dark:via-slate-900 dark:to-gray-900 shadow-md">
      <div
        className="absolute inset-x-0 top-0 h-1.5 bg-gradient-to-r from-indigo-500 via-sky-400 to-violet-500"
        aria-hidden="true"
      />

      <div className="p-4 pt-5 space-y-3.5">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-11 h-11 rounded-xl bg-gradient-to-br from-indigo-500 to-sky-500 text-white flex items-center justify-center shadow-sm">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 dark:bg-indigo-900/60 dark:text-indigo-200">
                Meeting invite
              </span>
              <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                isCancelled
                  ? 'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                  : appointmentInfo.status === 'CONFIRMED'
                    ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200'
                    : 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200'
              }`}>
                {isCancelled ? 'Cancelled' : appointmentInfo.status === 'CONFIRMED' ? 'Confirmed' : 'Pending'}
              </span>
            </div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mt-1.5 leading-snug">
              {appointmentInfo.title || 'Meeting'}
            </h3>
          </div>
        </div>

        <div className="rounded-xl bg-white/70 dark:bg-gray-950/40 px-3.5">
          <DetailRow label="When" value={formatWhen(appointmentInfo)} />
          <DetailRow label="Frequency" value={frequency} />
          <DetailRow label="Until" value={untilDate} />
          <DetailRow label="With" value={withNames} />
          <DetailRow label="Organizer" value={appointmentInfo.organizerName} />
          <DetailRow label="Notes" value={appointmentInfo.description} />
        </div>

        {appointmentInfo.meetingLink && !isCancelled && (
          <a
            href={appointmentInfo.meetingLink}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center w-full text-sm font-semibold text-white bg-gradient-to-r from-indigo-600 to-sky-500 hover:from-indigo-700 hover:to-sky-600 px-4 py-2.5 rounded-xl shadow-sm"
            onClick={(e) => e.stopPropagation()}
          >
            Join meeting
          </a>
        )}

        {rsvps.length > 0 && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="w-full text-left flex items-center justify-between gap-2 text-sm text-indigo-700 dark:text-indigo-300 font-medium"
            aria-expanded={expanded}
          >
            <span>{summaryParts.join(' · ') || 'No responses yet'}</span>
            <span className="text-xs text-gray-400 font-normal flex items-center gap-1">
              {expanded ? 'Hide' : 'Details'}
              <svg
                className={`w-4 h-4 transition-transform ${expanded ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </span>
          </button>
        )}

        {expanded && (
          <div className="space-y-2 rounded-xl border border-indigo-100 dark:border-indigo-900/50 bg-white/80 dark:bg-gray-950/40 p-3">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
              Who accepted / rejected
            </p>
            {rsvps.length === 0 ? (
              <p className="text-xs text-gray-400">No invitees yet</p>
            ) : (
              <ul className="divide-y divide-indigo-50 dark:divide-indigo-900/40">
                {rsvps.map((r) => {
                  const status = (r.status || 'PENDING').toUpperCase();
                  return (
                    <li
                      key={r.userId}
                      className="flex items-center justify-between gap-2 py-2 first:pt-0 last:pb-0"
                    >
                      <span className="truncate text-sm text-gray-800 dark:text-gray-200 font-medium">
                        {r.userName || 'User'}
                        {sameId(r.userId, currentUserId) ? ' (you)' : ''}
                        {sameId(r.userId, appointmentInfo.organizerId) ? ' · organizer' : ''}
                      </span>
                      <span className={`flex-shrink-0 px-2.5 py-1 rounded-full text-[11px] font-semibold ${
                        RSVP_BADGE[status] || RSVP_BADGE.PENDING
                      }`}>
                        {RSVP_LABEL[status] || status || 'Pending'}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}

        {canRespond && !isCancelled && (() => {
          const options = [
            { id: 'ACCEPTED', label: 'Accept', className: 'bg-emerald-600 hover:bg-emerald-700 text-white' },
            { id: 'TENTATIVE', label: 'Tentative', className: 'bg-sky-600 hover:bg-sky-700 text-white' },
            { id: 'REJECTED', label: 'Decline', className: 'bg-white dark:bg-gray-800 border border-rose-300 dark:border-rose-700 text-rose-700 dark:text-rose-300 hover:bg-rose-50 dark:hover:bg-rose-950/30' },
          ];
          return (
            <div className="pt-0.5 space-y-2.5" onClick={(e) => e.stopPropagation()}>
              {hasDecided && (
                <span className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold ${
                  RSVP_BADGE[myStatus] || RSVP_BADGE.PENDING
                }`}>
                  Your response: {RSVP_LABEL[myStatus]}
                </span>
              )}
              <div className="grid grid-cols-3 gap-2">
                {options.map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    disabled={saving || (hasDecided && myStatus === opt.id)}
                    onClick={() => handleRsvp(opt.id)}
                    className={`text-sm font-semibold px-3 py-2.5 rounded-xl disabled:opacity-50 transition-colors ${opt.className} ${
                      hasDecided && myStatus === opt.id ? 'ring-2 ring-offset-1 ring-current' : ''
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          );
        })()}

        {error && <p className="text-xs text-red-600 dark:text-red-400">{error}</p>}
      </div>
    </div>
  );
};

export default AppointmentTimelineCard;
